import { useQuery } from "@apollo/client/react";
import { SystemConfigDocument } from "../../../generated/graphql";

export function WeeklySyncSummary() {
  const { data, loading } = useQuery(SystemConfigDocument);

  if (loading) return <div>Loading...</div>;

  const lastSync = data?.systemConfig?.lastSyncResult;

  if (!lastSync) {
    return (
      <div className="panel p-6">
        <h3 className="h5 mb-2">Weekly Sync</h3>
        <p className="body-sm text-gray-600 dark:text-gray-400">
          No sync has been performed yet. The weekly sync runs every Sunday at 2:00 AM.
        </p>
      </div>
    );
  }

  const hasErrors = (lastSync.errors?.length || 0) > 0;
  const syncDate = lastSync.syncedAt ? new Date(lastSync.syncedAt) : null;

  return (
    <div className="panel p-6">
      <div className="mb-4">
        <h3 className="h5 mb-1">Weekly Sync</h3>
        <div className="body-sm text-gray-600 dark:text-gray-400">
          <div>
            Last sync: <strong>{lastSync.syncType || "unknown"}</strong> sync on{" "}
            <strong>
              {syncDate
                ? syncDate.toLocaleString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "Unknown date"}
            </strong>
          </div>
        </div>
      </div>

      {/* Schools Discovered */}
      <div className="p-3 bg-accent-500/10 border border-accent-500 rounded-lg mb-3">
        <div className="label text-accent-400 mb-2">✅ Schools discovered from MyBestUser</div>
        <div className="caption text-accent-300 ml-2">
          <div>
            • Total qualifying schools: <strong>{lastSync.total || 0}</strong>
          </div>
          <div>
            • Schools added: <strong>{lastSync.added || 0}</strong>
          </div>
          <div>
            • Schools updated: <strong>{lastSync.updated || 0}</strong>
          </div>
          <div>
            • Schools deactivated: <strong>{lastSync.deactivated || 0}</strong>
          </div>
        </div>
      </div>

      {/* Classrooms Synced */}
      <div className="p-3 bg-accent-500/10 border border-accent-500 rounded-lg mb-3">
        <div className="label text-accent-400 mb-2">✅ Classrooms synced for each school</div>
        <div className="caption text-accent-300 ml-2">
          Total classrooms synced: <strong>{lastSync.classroomsSynced || 0}</strong>
        </div>
      </div>

      {/* Memberships Created/Updated */}
      <div className="p-3 bg-accent-500/10 border border-accent-500 rounded-lg mb-3">
        <div className="label text-accent-400 mb-2">✅ Memberships created/updated</div>
        <div className="caption text-accent-300 ml-2">
          <div>
            • Memberships created: <strong>{lastSync.membershipsCreated || 0}</strong>
          </div>
          <div>
            • Memberships updated: <strong>{lastSync.membershipsUpdated || 0}</strong>
          </div>
        </div>
      </div>

      {/* Sessions Invalidated */}
      <div
        className={`p-3 bg-accent-500/10 border border-accent-500 rounded-lg ${hasErrors ? "mb-3" : ""}`}
      >
        <div className="label text-accent-400 mb-2">✅ Sessions invalidated for affected users</div>
        <div className="caption text-accent-300 ml-2">
          Sessions invalidated: <strong>{lastSync.sessionsInvalidated || 0}</strong>
        </div>
      </div>

      {/* Errors */}
      {hasErrors && (
        <div className="p-3 bg-error-500/10 border border-error-500 rounded-lg">
          <div className="label text-error-300 mb-2">
            ⚠️ Errors during sync ({lastSync.errors?.length || 0})
          </div>
          <div className="max-h-[200px] overflow-y-auto gradient-scrollbar caption text-error-200">
            {lastSync.errors?.map((error, index) => {
              const errorDate = error.timestamp ? new Date(error.timestamp) : null;
              const errorKey = `${error.schoolId || "unknown"}-${error.timestamp || index}`;
              return (
                <div
                  key={errorKey}
                  className={`ml-2 ${index < (lastSync.errors?.length || 0) - 1 ? "mb-2 pb-2 border-b border-error-500/30" : ""}`}
                >
                  {error.schoolName && (
                    <div className="font-semibold">School: {error.schoolName}</div>
                  )}
                  <div>Error: {error.error}</div>
                  {errorDate && (
                    <div className="caption text-error-300 mt-0.5">
                      Time: {errorDate.toLocaleString()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Info */}
      <div className="mt-4 caption text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-800 pt-3">
        <div className="italic">
          The weekly sync runs automatically every Sunday at 2:00 AM. It checks MyBestUser for all
          active schools with required products/applications, validates active contracts and school
          status, discovers new qualifying schools, updates existing schools, deactivates schools
          that no longer qualify, syncs classrooms and memberships, and invalidates sessions for
          affected users.
        </div>
      </div>
    </div>
  );
}
